import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Users, UserCheck, Ticket, Wallet, RefreshCw } from 'lucide-react';
import { adminApi, formatCurrency, formatNumber, formatDate } from '@kardume/services';
import { theme } from '@kardume/theme';
import type { MerchantCustomerDto } from '@kardume/types';
import {
  PageHeader,
  StatCard,
  SearchInput,
  ActionButton,
  Table,
  TableContainer,
  Pagination,
  formatPhone,
} from '@kardume/ui';
import { useAsync } from '../utils/useAsync';
import { useDebouncedValue } from '../utils/useDebouncedValue';
import DataState from '../components/DataState';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = 20;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const Toolbar = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
  align-items: center;

  > *:first-child {
    flex: 1;
    min-width: 240px;
  }
`;

const Muted = styled.span`
  color: ${(p) => p.theme.textMuted};
`;

/** Merchant panel screen listing the store's customers (server-side paged). */
const CustomersPage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), 350);

  const { data, loading, error, reload } = useAsync(
    () =>
      adminApi.listCustomers({
        page,
        size: pageSize,
        query: debouncedSearch || undefined,
      }),
    [page, pageSize, debouncedSearch]
  );

  const summaryState = useAsync(() => adminApi.getCustomerSummary(), []);
  const summary = summaryState.data;

  const rows = useMemo<MerchantCustomerDto[]>(() => data?.content ?? [], [data]);

  const reloadAll = () => {
    reload();
    summaryState.reload();
  };

  const onSearch = (value: string) => {
    setSearch(value);
    setPage(0);
  };
  const onPageSize = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  return (
    <>
      <PageHeader
        title="Clientes"
        subtitle="Consumidores que já interagiram com a sua loja no Kardume Urbano."
      />

      <StatsGrid>
        <StatCard
          label="Total de clientes"
          value={formatNumber(summary?.total ?? 0)}
          icon={<Users size={18} />}
        />
        <StatCard
          label="Ativos (30 dias)"
          value={formatNumber(summary?.activeLast30Days ?? 0)}
          icon={<UserCheck size={18} />}
          iconColor={theme.success}
        />
        <StatCard
          label="Resgates totais"
          value={formatNumber(summary?.totalRedemptions ?? 0)}
          icon={<Ticket size={18} />}
          iconColor={theme.info}
        />
        <StatCard
          label="Cashback disponível"
          value={formatCurrency(summary?.totalCashbackBalance ?? 0)}
          icon={<Wallet size={18} />}
          iconColor={theme.warning}
        />
      </StatsGrid>

      <Toolbar>
        <SearchInput
          placeholder="Buscar por nome ou e-mail..."
          value={search}
          onChange={(e) => onSearch(e.target.value)}
        />
        <ActionButton title="Atualizar" onClick={reloadAll}>
          <RefreshCw size={16} />
        </ActionButton>
      </Toolbar>

      <DataState
        loading={loading}
        error={error}
        onRetry={reload}
        isEmpty={!data || rows.length === 0}
        emptyTitle="Nenhum cliente encontrado"
        emptyDescription="Assim que consumidores resgatarem suas promoções, eles aparecerão aqui."
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Cliente</th>
                <th>Telefone</th>
                <th>Resgates</th>
                <th>Saldo de cashback</th>
                <th>Última visita</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((customer) => (
                <tr key={customer.customerId}>
                  <td>
                    <strong>
                      {customer.firstName} {customer.lastName}
                    </strong>
                    <br />
                    <Muted>{customer.email}</Muted>
                  </td>
                  <td>{customer.phone ? formatPhone(customer.phone) : <Muted>-</Muted>}</td>
                  <td>{formatNumber(customer.redemptions)}</td>
                  <td>{formatCurrency(customer.cashbackBalance)}</td>
                  <td>
                    {customer.lastVisitAt ? formatDate(customer.lastVisitAt) : <Muted>-</Muted>}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>

        <Pagination
          currentPage={page + 1}
          totalPages={data?.totalPages ?? 1}
          totalItems={data?.totalElements}
          pageSize={pageSize}
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onPageChange={(next) => setPage(next - 1)}
          onPageSizeChange={onPageSize}
        />
      </DataState>
    </>
  );
};

export default CustomersPage;
